import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCardIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, PlusIcon, SearchIcon, RefreshCwIcon, ChevronDownIcon, CheckIcon, BarChart2Icon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { useNavigate } from 'react-router-dom';
function StatusDropdown({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{
    value: string;
    label: string;
    dot?: string;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = options.find(o => o.value === value);
  return <div ref={ref} className="relative" data-id="element-14849" data-mp="3lo2mgmrprbkw">
      <button type="button" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(!open)} className="h-[42px] px-3 pr-8 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2 min-w-[150px] focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-14850" data-mp="3qgvw9p21ha3u">
        {selected?.dot && <span className={`w-2 h-2 rounded-full ${selected.dot} flex-shrink-0`} data-id="element-14851" data-mp="d8sqzxibx4m3" />}
        <span className="truncate" data-id="element-14852" data-mp="3vf70nk9keseo">{selected?.label}</span>
        <ChevronDownIcon size={14} className={`absolute right-2.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-14853" data-mp="48w686z28pwb" />
      </button>

      {open && <div className="absolute z-20 mt-1 right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-14854" data-mp="2p065hgsd6e95">
          {options.map(opt => <button key={opt.value} type="button" onClick={() => {
        onChange(opt.value);
        setOpen(false);
      }} className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${value === opt.value ? 'bg-[#E2231A]/5 font-medium' : 'text-gray-700'}`} data-id="element-14855" data-mp="ulirgmzl84b4">
              <div className="flex items-center gap-2" data-id="element-14856" data-mp="6ttv8vjnavow">
                {opt.dot && <span className={`w-2 h-2 rounded-full ${opt.dot} flex-shrink-0`} data-id="element-14857" data-mp="6w6oi4uussh9" />}
                <span data-id="element-14858" data-mp="3tzbgklld9nyf">{opt.label}</span>
              </div>
              {value === opt.value && <CheckIcon size={14} className="text-[#9F140F] flex-shrink-0" data-id="element-14859" data-mp="1w91rwk5cze44" />}
            </button>)}
        </div>}
    </div>;
}
interface Terminal {
  id: string;
  name: string;
  serialNumber: string;
  model: string;
  brand: string;
  status: 'online' | 'offline' | 'maintenance';
  location: string;
  lastConnection: string;
  transactionVolume: number;
  batteryLevel?: number;
  firmwareVersion: string;
  companyId: string;
}
const terminals: Terminal[] = [{
  id: 'term-001',
  name: 'Main Counter 1',
  serialNumber: 'VFN-V240m-12345',
  model: 'Verifone V240m',
  brand: 'Verifone',
  status: 'online',
  location: 'Checkout Area',
  lastConnection: '2 minutes ago',
  transactionVolume: 12450,
  batteryLevel: 85,
  firmwareVersion: '4.3.1',
  companyId: 'submerchant-kl-001'
}, {
  id: 'term-002',
  name: 'Main Counter 2',
  serialNumber: 'VFN-V400m-98765',
  model: 'Verifone V400m',
  brand: 'Verifone',
  status: 'online',
  location: 'Checkout Area',
  lastConnection: '5 minutes ago',
  transactionVolume: 9870,
  batteryLevel: 72,
  firmwareVersion: '2.1.0',
  companyId: 'submerchant-kl-001'
}, {
  id: 'term-003',
  name: 'Self-Checkout 1',
  serialNumber: 'VFN-T650p-34567',
  model: 'Verifone T650p',
  brand: 'Verifone',
  status: 'offline',
  location: 'Self-Service Zone',
  lastConnection: '3 days ago',
  transactionVolume: 5430,
  batteryLevel: 0,
  firmwareVersion: '3.0.2',
  companyId: 'submerchant-kl-002'
}, {
  id: 'term-004',
  name: 'Pharmacy Counter',
  serialNumber: 'VFN-M400-56789',
  model: 'Verifone M400',
  brand: 'Verifone',
  status: 'online',
  location: 'Pharmacy Section',
  lastConnection: '17 minutes ago',
  transactionVolume: 3250,
  batteryLevel: 45,
  firmwareVersion: '4.2.1',
  companyId: 'submerchant-kl-002'
}, {
  id: 'term-005',
  name: 'Customer Service',
  serialNumber: 'VFN-V200c-54321',
  model: 'Verifone V200c',
  brand: 'Verifone',
  status: 'maintenance',
  location: 'Service Desk',
  lastConnection: '1 day ago',
  transactionVolume: 7650,
  batteryLevel: 100,
  firmwareVersion: '1.9.5',
  companyId: 'submerchant-kl-003'
}, {
  id: 'term-006',
  name: 'Fresh Produce',
  serialNumber: 'VFN-e280-67890',
  model: 'Verifone e280',
  brand: 'Verifone',
  status: 'online',
  location: 'Produce Section',
  lastConnection: '1 hour ago',
  transactionVolume: 2180,
  firmwareVersion: '2.5.0',
  companyId: 'submerchant-kl-003'
}, {
  id: 'term-007',
  name: 'Deli Counter',
  serialNumber: 'VFN-P400-24680',
  model: 'Verifone P400',
  brand: 'Verifone',
  status: 'online',
  location: 'Deli & Bakery',
  lastConnection: '10 minutes ago',
  transactionVolume: 8920,
  firmwareVersion: '3.1.2',
  companyId: 'submerchant-kl-004'
}, {
  id: 'term-008',
  name: 'Express Lane',
  serialNumber: 'VFN-e355-13579',
  model: 'Verifone e355',
  brand: 'Verifone',
  status: 'offline',
  location: 'Quick Checkout',
  lastConnection: '2 days ago',
  transactionVolume: 1540,
  batteryLevel: 0,
  firmwareVersion: '2.0.3',
  companyId: 'submerchant-kl-004'
}];
const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
const getStatusBadge = (status: 'online' | 'offline' | 'maintenance') => {
  const styles = {
    online: 'bg-green-50 text-green-700',
    offline: 'bg-red-50 text-red-700',
    maintenance: 'bg-amber-50 text-amber-700'
  };
  const icons = {
    online: <CheckCircleIcon size={11} className="mr-1" data-id="element-14860" data-mp="3iacfmr9jgx58" />,
    offline: <XCircleIcon size={11} className="mr-1" data-id="element-14861" data-mp="3e6y2c8cziuvh" />,
    maintenance: <AlertTriangleIcon size={11} className="mr-1" data-id="element-14862" data-mp="3e9k8q4o534c4" />
  };
  const labels = {
    online: 'Online',
    offline: 'Offline',
    maintenance: 'Maintenance'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status]}`} data-id="element-14863" data-mp="22t2bi2cpwxof">
      {icons[status]}
      {labels[status]}
    </span>;
};
const getBatteryBar = (level?: number) => {
  if (level === undefined) return <span className="text-xs text-gray-400" data-id="element-14864" data-mp="i9qgw46aoj4h">—</span>;
  const color = level < 20 ? 'bg-red-500' : level < 50 ? 'bg-amber-500' : 'bg-green-500';
  return <div className="flex items-center gap-1.5" data-id="element-14865" data-mp="mak56ynnfzxw">
      <div className="w-7 h-2.5 border border-gray-300 rounded-[3px] overflow-hidden relative" data-id="element-14866" data-mp="r33jxyy2p14a">
        <div className={`absolute inset-y-0 left-0 ${color} rounded-[2px]`} style={{
        width: `${level}%`
      }} data-id="element-14867" data-mp="w26e2ocilmxh" />
      </div>
      <span className="text-xs text-gray-500" data-id="element-14868" data-mp="112kna9pur2x3">{level}%</span>
    </div>;
};
const brandModelsMap: Record<string, string[]> = {
  Verifone: ['V240m', 'V400m', 'T650p', 'M400', 'V200c', 'e280', 'P400', 'e355'],
  Ingenico: ['ISC250', 'Lane/7000', 'Move/5000', 'Desk/5000', 'Self/400'],
  PAX: ['A920', 'D210', 'S300', 'E500', 'S80'],
  Newland: ['MT66', 'N5', 'N6', 'MS838', 'MS758']
};
export function PhysicalTerminals() {
  const {
    t
  } = useTranslation('terminals');
  const {
    selectedCompany
  } = useCompany();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  // Form state for Request Terminal Modal
  const [location, setLocation] = useState('');
  const [brand, setBrand] = useState('Verifone');
  const [model, setModel] = useState(brandModelsMap['Verifone'][0]);
  const [terminalName, setTerminalName] = useState('');
  const navigate = useNavigate();
  // Update model options when brand changes
  useEffect(() => {
    const models = brandModelsMap[brand] || [];
    if (!models.includes(model)) {
      setModel(models[0] || '');
    }
  }, [brand, model]);
  // Filter terminals by selected company
  const companyFilteredTerminals = selectedCompany.type === 'master' ? terminals : terminals.filter(t => t.companyId === selectedCompany.id);
  const filteredTerminals = companyFilteredTerminals.filter(t => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = t.name.toLowerCase().includes(q) || t.serialNumber.toLowerCase().includes(q) || t.model.toLowerCase().includes(q) || t.location.toLowerCase().includes(q);
    return matchesSearch && (statusFilter === 'all' || t.status === statusFilter);
  });
  const totalDevices = companyFilteredTerminals.length;
  const onlineDevices = companyFilteredTerminals.filter(t => t.status === 'online').length;
  const totalVolume = companyFilteredTerminals.reduce((s, t) => s + t.transactionVolume, 0);
  const openRequestModal = () => setIsRequestModalOpen(true);
  const closeRequestModal = () => setIsRequestModalOpen(false);
  const handleSubmitRequest = () => {
    const timestamp = Date.now();
    const requestData = {
      location,
      brand,
      model,
      terminalName
    };
    closeRequestModal();
    navigate(`/requests/TERM-REQ-${timestamp}`, {
      state: requestData
    });
  };
  return <div className="space-y-5" data-id="element-14869" data-mp="25m3w0l6nczkj">
      {/* ─── STATS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-14870" data-mp="25lkolyemhtkr">
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14871" data-mp="1rljdzr8k37s3">
          <div className="h-10 w-10 rounded-lg bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-14872" data-mp="1wkmmjlnqvw4t">
            <CreditCardIcon size={18} className="text-[#9F140F]" data-id="element-14873" data-mp="1hv89o3kymwko" />
          </div>
          <div data-id="element-14874" data-mp="1mojiga9c1nf8">
            <p className="text-xs text-gray-500" data-id="element-14875" data-mp="1mp2pux1cwtf0">
              {t('pages.PhysicalTerminals.total_terminals')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14876" data-mp="18u0f9wlkefvv">{totalDevices}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14877" data-mp="1cvc3pyspcv2h">
          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-14878" data-mp="13x0hsa2s6lgd">
            <CheckCircleIcon size={18} className="text-green-600" data-id="element-14879" data-mp="kaym2wvrpouq" />
          </div>
          <div data-id="element-14880" data-mp="kd1ko5azkqb2">
            <p className="text-xs text-gray-500" data-id="element-14881" data-mp="fcdjwrwygv3n">
              {t('pages.PhysicalTerminals.online_terminals')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14882" data-mp="uafpt8rglso2">{onlineDevices}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14883" data-mp="p99z1ghd0fh6">
          <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-14884" data-mp="138rpe0ab00sn">
            <BarChart2Icon size={18} className="text-purple-600" data-id="element-14885" data-mp="yagedm89vk8u" />
          </div>
          <div data-id="element-14886" data-mp="1cyugfuxv74oc">
            <p className="text-xs text-gray-500" data-id="element-14887" data-mp="1czcmfjabwx9g">
              {t('pages.Connect.total_volume')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14888" data-mp="182vxwnz7wgvu">
              {getTenant().currency.code} {totalVolume.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ─── SEARCH & FILTER ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-14889" data-mp="1p26s24axzfgw">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-14890" data-mp="1p402luooeju6">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-14891" data-mp="1u2bdm8qpj0dz">
            <CreditCardIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-14892" data-mp="1yztdzaqns7ad" />
            {t('pages.PhysicalTerminals.physical_terminals')}
          </h2>
        </div>
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-3" data-id="element-14893" data-mp="23t61tinika22">
          <div className="relative flex-1" data-id="element-14894" data-mp="27rvbp19cf9gy">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-14895" data-mp="2crrewya7uwvg" />

            <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.PhysicalTerminals.attrs.search_by_name_serial_model_or')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-14896" data-mp="2hrvyix6f87pw" />
          </div>
          <div className="flex items-center gap-2" data-id="element-14897" data-mp="sdr42cnhde5w">
            <StatusDropdown value={statusFilter} onChange={setStatusFilter} options={[{
            value: 'all',
            label: 'All status'
          }, {
            value: 'online',
            label: 'Online',
            dot: 'bg-green-500'
          }, {
            value: 'offline',
            label: 'Offline',
            dot: 'bg-red-500'
          }, {
            value: 'maintenance',
            label: 'Maintenance',
            dot: 'bg-amber-500'
          }]} data-id="element-14898" data-mp="35n7mv1qhm3wr" />

            <button className="h-[42px] px-3 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-14899" data-mp="3akr0q0j9weo6">
              <RefreshCwIcon size={13} data-id="element-14900" data-mp="1z52e5a9xlhnw" />
              {t('pages.Connect.refresh')}
            </button>
            <button type="button" onClick={openRequestModal} className="h-[42px] px-3 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-14901" data-mp="3n1qprmem3lhn">
              <PlusIcon size={13} data-id="element-14902" data-mp="3j0opr3cvf5em" />
              {t('pages.PhysicalTerminals.request_terminal')}
            </button>
          </div>
        </div>
      </div>

      {/* ─── TERMINAL GRID ─── */}
      {filteredTerminals.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-14903" data-mp="elou190oqsm8">
          {filteredTerminals.map(terminal => <div key={terminal.id} className="bg-white rounded-xl border border-gray-200" data-id="element-14904" data-mp="pl6tzo8bkj9">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between" data-id="element-14905" data-mp="2lcoe4d21kqtq">
                <div className="flex items-center gap-2.5 min-w-0" data-id="element-14906" data-mp="2qdc5x0y3hbp0">
                  <div className="h-8 w-8 rounded-lg bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-14907" data-mp="2isi7fxm1mm9h">
                    <CreditCardIcon size={14} className="text-[#9F140F]" data-id="element-14908" data-mp="2dsvnz6fmg9s8" />
                  </div>
                  <div className="min-w-0" data-id="element-14909" data-mp="2rsn6nkh32eck">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-14910" data-mp="2rrc41nzdjcn1">
                      {terminal.name}
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-14911" data-mp="1z499t81qdo2l">
                      {terminal.model}
                    </p>
                  </div>
                </div>
                {getStatusBadge(terminal.status)}
              </div>

              {/* Details — edge-to-edge rows */}
              <div data-id="element-14912" data-mp="243lsdglkk8zd">
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14913" data-mp="2432jepdwcuxh">
                  <span className="text-xs text-gray-500" data-id="element-14914" data-mp="1gd884c987efc">
                    {t('pages.PhysicalTerminals.serial')}
                  </span>
                  <span className="text-xs font-medium text-gray-900 font-mono" data-id="element-14915" data-mp="1bdltpzhnvs4y">
                    {terminal.serialNumber}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14916" data-mp="3gey2iptzh4nf">
                  <span className="text-xs text-gray-500" data-id="element-14917" data-mp="3l8amunrmqpad">
                    {t('pages.PhysicalTerminals.location')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14918" data-mp="2sjd7asesz6lb">
                    {terminal.location}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14919" data-mp="2xk0wd8jh1dcw">
                  <span className="text-xs text-gray-500" data-id="element-14920" data-mp="32j408pnptdja">
                    {t('pages.PhysicalTerminals.last_connection')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14921" data-mp="hpqull8ybjfk">
                    {terminal.lastConnection}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14922" data-mp="26aesusv32jqp">
                  <span className="text-xs text-gray-500" data-id="element-14923" data-mp="22930wgoqlmfc">
                    {t('pages.Connect.volume')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14924" data-mp="1xh2waaxq25cf">
                    {getTenant().currency.code}{' '}
                    {terminal.transactionVolume.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14925" data-mp="1nhu3vmii8ys6">
                  <span className="text-xs text-gray-500" data-id="element-14926" data-mp="1imy8qdhz1vau">
                    {t('pages.PhysicalTerminals.firmware')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14927" data-mp="19kxg5txonxfi">
                    {t('pages.PhysicalTerminals.v')}
                    {terminal.firmwareVersion}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5" data-id="element-14928" data-mp="33zhw5tb274sk">
                  <span className="text-xs text-gray-500" data-id="element-14929" data-mp="2csklz89sx4kg">
                    {t('pages.PhysicalTerminals.battery')}
                  </span>
                  {getBatteryBar(terminal.batteryLevel)}
                </div>
              </div>

              {/* Actions */}
              <div className="px-5 py-3 border-t border-gray-200 flex justify-between" data-id="element-14930" data-mp="282485n22jgxk">
                <button className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-14931" data-mp="27zrkct9mqccl">
                  {t('pages.PhysicalTerminals.view_details')}
                </button>
                <button className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-14932" data-mp="1y1lj8dvnda8s">
                  {t('pages.PhysicalTerminals.update_firmware')}
                </button>
              </div>
            </div>)}
        </div> : <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center text-center" data-id="element-14933" data-mp="3eipud66dkvw7">
          <CreditCardIcon size={24} className="text-gray-400 mb-3" data-id="element-14934" data-mp="3jf6oqi4646hq" />
          <p className="text-sm font-medium text-gray-700" data-id="element-14935" data-mp="17upgretw87cd">
            {t('pages.PhysicalTerminals.no_terminals_found')}
          </p>
          <p className="text-xs text-gray-500 mt-1 max-w-xs" data-id="element-14936" data-mp="12t00nkrtd3hf">
            {t('pages.PhysicalTerminals.no_terminals_match_your_search_criteria')}
          </p>
        </div>}

      {/* Request Terminal Modal */}
      {isRequestModalOpen && <div role="dialog" aria-modal="true" className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50" data-id="element-14937" data-mp="1qikmcbfcqeue">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-id="element-14938" data-mp="20hjxq1ri4ol3">
            <h3 className="text-lg font-semibold mb-4" data-id="element-14939" data-mp="1vjrw2g121o3p">
              {t('pages.PhysicalTerminals.request_terminal')}
            </h3>
            <form onSubmit={e => {
          e.preventDefault();
          handleSubmitRequest();
        }} data-id="element-14940" data-mp="65d8hz1rw79s">
              <div className="mb-4" data-id="element-14941" data-mp="1uasul49zh75o">
                <label htmlFor="location" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14942" data-mp="1u9rcwgygpmez">
                  Location
                </label>
                <input type="text" id="location" value={location} onChange={e => setLocation(e.target.value)} className={inputClass} required data-id="element-14943" data-mp="23zauljafva89" />
              </div>
              <div className="mb-4" data-id="element-14944" data-mp="16dx1fno1gvkd">
                <label htmlFor="brand" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14945" data-mp="16eycvt8yvk2m">
                  Brand
                </label>
                <select id="brand" value={brand} onChange={e => setBrand(e.target.value)} className={inputClass} required data-id="element-14946" data-mp="1gd4d86f4ki5d">
                  <option value="Verifone" data-id="element-14947" data-mp="2z9z7v9qddyao">Verifone</option>
                  <option value="Ingenico" data-id="element-14948" data-mp="2z8efnqrj2rfz">Ingenico</option>
                  <option value="PAX" data-id="element-14949" data-mp="3492egym00ckr">PAX</option>
                  <option value="Newland" data-id="element-14950" data-mp="346pru85ape1d">Newland</option>
                </select>
              </div>
              <div className="mb-4" data-id="element-14951" data-mp="2qbdv5v7kxdht">
                <label htmlFor="model" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14952" data-mp="2ua37rlksmr0e">
                  Model
                </label>
                <select id="model" value={model} onChange={e => setModel(e.target.value)} className={inputClass} required data-id="element-14953" data-mp="3mztom2dt8707">
                  {(brandModelsMap[brand] || []).map(m => <option key={m} value={m} data-id="element-14954" data-mp="4t5gs4rdl5qu">
                      {m}
                    </option>)}
                </select>
              </div>
              <div className="mb-4" data-id="element-14955" data-mp="9m8emp36r1cu">
                <label htmlFor="terminalName" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14956" data-mp="jgnq5gfvuojl">
                  Terminal Name
                </label>
                <input type="text" id="terminalName" value={terminalName} onChange={e => setTerminalName(e.target.value)} className={inputClass} required data-id="element-14957" data-mp="aemuup47mckk" />
              </div>
              <div className="flex justify-end gap-3" data-id="element-14958" data-mp="3rv4nft4djebv">
                <button type="button" onClick={closeRequestModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition" data-id="element-14959" data-mp="3nulw006e8aa7">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#9F140F] hover:bg-[#8C6C00] rounded-md transition" data-id="element-14960" data-mp="38xbm2v9efcgl">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
}